import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePatientRecord } from '../state/data/patient';
import {
  classifyBp, classifyHr, BP_CLASS_LABELS, HR_CLASS_LABELS, BP_CLASS_TIER, HR_CLASS_TIER,
  BP_CLASS_DETAIL, HR_CLASS_DETAIL, isHrAlerting, findFocusPage,
} from '../logic/bpReadings';

const PAGE_SIZE = 20;

// Badge palette. Uses witems' designed text/background PAIRS (--wi-text-primary-X on
// --wi-background-X, ~10.4:1 in both themes and correctly inverted for dark) — never the
// --wi-status-X fills as text. --wi-status-moderate is #ffc907: 1.54:1 on white, i.e. a fill
// colour, and as pill text the Tier-2 tachycardia badge was effectively invisible.
// `edge` keeps the saturated status hue for the Tier-1 border, so urgency still reads at a
// glance while the text stays legible. NB --wi-text-primary-high has no v2-light value, so
// the `high` family is deliberately unused here — it would fail silently.
const PALETTE = {
  info:     { fg: 'var(--wi-text-primary-info)',     bg: 'var(--wi-background-info)',     edge: 'var(--wi-status-info)' },
  good:     { fg: 'var(--wi-text-primary-good)',     bg: 'var(--wi-background-good)',     edge: 'var(--wi-status-good)' },
  moderate: { fg: 'var(--wi-text-primary-moderate)', bg: 'var(--wi-background-moderate)', edge: 'var(--wi-status-moderate)' },
  bad:      { fg: 'var(--wi-text-primary-bad)',      bg: 'var(--wi-background-bad)',      edge: 'var(--wi-status-bad)' },
};

// Hue follows the protocol's own table: green = no tier, amber = Tier 2, red = Tier 1 high,
// blue = Tier 1 LOW (a red ramp misreads a low reading as badly as green hid it).
const BADGE = {
  hypotension: PALETTE.info,
  controlled:  PALETTE.good,
  elevated:    PALETTE.good,
  stage2:      PALETTE.moderate,
  crisis:      PALETTE.bad,
  emergency:   PALETTE.bad,
};

const HR_BADGE = {
  bradycardia:       PALETTE.info,
  tachy_mild:        PALETTE.moderate,
  tachy_severe:      PALETTE.bad,
  tachy_very_severe: PALETTE.bad,
};

const badgeStyle = (c, tier) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontSize: 11, fontWeight: tier === 1 ? 700 : 600, padding: '3px 10px', borderRadius: 12,
  background: c.bg, color: c.fg, whiteSpace: 'nowrap',
  border: `1px solid ${tier === 1 ? c.edge : 'transparent'}`,
});

function Pill({ label, detail, palette, tier }) {
  return (
    <span title={detail} style={badgeStyle(palette, tier)}>
      {tier === 1 ? '⚠ ' : ''}{label}
    </span>
  );
}

// Every finding for the reading, one named pill each. The heart rate needs its OWN pill
// rather than a tinted number in the HR column: WF3 raises a Tier-1 task on 175 bpm, and a
// row whose only verdict read "Controlled" (the BP band) contradicted its own alert. HR only
// counts when it came off the BPM — pairBpReadings guarantees that (it attaches type 11 from
// the BP session only), matching WF3's BPM-only rule.
function AnalysisBadges({ systolic, diastolic, hr }) {
  const bpCls = classifyBp(systolic, diastolic);
  const hrCls = classifyHr(hr);
  const showHr = isHrAlerting(hrCls);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
      <Pill
        label={BP_CLASS_LABELS[bpCls]}
        detail={BP_CLASS_DETAIL[bpCls]}
        palette={BADGE[bpCls]}
        tier={BP_CLASS_TIER[bpCls]}
      />
      {showHr && (
        <Pill
          label={HR_CLASS_LABELS[hrCls]}
          detail={`${hr} bpm — ${HR_CLASS_DETAIL[hrCls]}`}
          palette={HR_BADGE[hrCls]}
          tier={HR_CLASS_TIER[hrCls]}
        />
      )}
    </div>
  );
}

// Invalid/absent IANA zone falls back to the viewer's clock — the header note stays honest.
function makeTimeFormatter(timezone) {
  const opts = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
  if (timezone) {
    try {
      const f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: timezone });
      return { format: (epoch) => f.format(new Date(epoch * 1000)), patientTz: timezone };
    } catch { /* fall through to viewer time */ }
  }
  const f = new Intl.DateTimeFormat('en-US', opts);
  return { format: (epoch) => f.format(new Date(epoch * 1000)), patientTz: null };
}

const TH = {
  textAlign: 'left', padding: '8px 12px', fontSize: 10, fontWeight: 600,
  color: 'var(--wi-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '1px solid var(--wi-divider)', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, background: 'var(--wi-background-secondary)', zIndex: 1,
};
const TD = {
  padding: '9px 12px', fontSize: 13, borderBottom: '1px solid var(--wi-divider)',
  fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle',
};

// Rendered only while open (conditional mount) — the focusRange → initial-page
// derivation lives in the useState initializer and must re-run on each open.
export default function BpReadingsListModal({
  userid, readings, rangeDays, truncated = false, focusRange = null, onClose,
}) {
  const rows = useMemo(() => readings.slice().reverse(), [readings]); // asc → newest first
  const [page, setPage] = useState(() => findFocusPage(rows, focusRange, PAGE_SIZE));

  const record = usePatientRecord(userid);
  const timeFmt = makeTimeFormatter(record.data?.timezone || null);

  // Clamp at render — readings can refetch while open and shrink the page count.
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const inFocus = (epoch) => focusRange != null && epoch >= focusRange[0] && epoch < focusRange[1];

  // Capture + stopPropagation: the patient drawer's own window Escape listener
  // NAVIGATES it closed — a bubble listener here would close both.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const firstIdx = safePage * PAGE_SIZE + 1;
  const lastIdx = Math.min((safePage + 1) * PAGE_SIZE, rows.length);

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-label="Blood pressure readings"
        style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header" style={{ marginBottom: 16 }}>
          <h2 className="header3" style={{ margin: 0 }}>Blood pressure readings</h2>
          <button className="modal-close" aria-label="Close" onClick={onClose}>×</button>
          <div style={{ fontSize: 12, color: 'var(--wi-text-secondary)', marginTop: 6 }}>
            {rows.length} reading{rows.length === 1 ? '' : 's'} · last {rangeDays} days ·{' '}
            {timeFmt.patientTz
              ? `times in the patient’s timezone (${timeFmt.patientTz})`
              : 'times in your local time'}
          </div>
        </header>

        {rows.length === 0 ? (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <span className="detail1 text-secondary">No readings in this period</span>
          </div>
        ) : (
          <div style={{ overflow: 'auto', flex: '1 1 auto', minHeight: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th scope="col" style={TH}>Time</th>
                  <th scope="col" style={{ ...TH, textAlign: 'right' }}>SYS (mmHg)</th>
                  <th scope="col" style={{ ...TH, textAlign: 'right' }}>DIA (mmHg)</th>
                  <th scope="col" style={{ ...TH, textAlign: 'right' }}>HR (bpm)</th>
                  <th scope="col" style={TH}>Analysis</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr
                    key={`${r.epoch}-${r.grpid ?? ''}`}
                    aria-current={inFocus(r.epoch) ? 'true' : undefined}
                    style={inFocus(r.epoch) ? { background: 'var(--wi-filter-active-bg)' } : undefined}
                  >
                    <td style={{ ...TD, color: 'var(--wi-text-secondary)', whiteSpace: 'nowrap' }}>
                      {timeFmt.format(r.epoch)}
                    </td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }}>{Math.round(r.systolic)}</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }}>{Math.round(r.diastolic)}</td>
                    <td style={{ ...TD, textAlign: 'right', color: r.hr == null ? 'var(--wi-text-placeholder)' : undefined }}>
                      {r.hr == null ? '—' : r.hr}
                    </td>
                    <td style={TD}>
                      <AnalysisBadges systolic={r.systolic} diastolic={r.diastolic} hr={r.hr} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {truncated && (
          <div style={{ fontSize: 11, color: 'var(--wi-text-secondary)', fontStyle: 'italic', marginTop: 10 }}>
            Some readings in this period exceeded the fetch limit and aren’t shown.
          </div>
        )}

        {pageCount > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
            <button
              className="btn-secondary"
              style={{ fontSize: 12, padding: '6px 14px' }}
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
            >
              ‹ Prev
            </button>
            <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
              {firstIdx}–{lastIdx} of {rows.length} · page {safePage + 1} of {pageCount}
            </span>
            <button
              className="btn-secondary"
              style={{ fontSize: 12, padding: '6px 14px' }}
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage(safePage + 1)}
            >
              Next ›
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
